import { createSelector } from '@app/utils/createSelector'
import { mergeMaps } from '@app/utils/mergeMaps'
import { normalize } from '@app/utils/normalizer'

import type { ApiMetaTags, ApiUser } from '@app/packages/api/v2/types/entities'

import { createReduxSlice } from '@app/store/redux_slice'

const userMetaTagsSlice = createReduxSlice<{ [key: string]: ApiMetaTags }>('user_meta_tags')

export const userMetaTagsSelector = createSelector([userMetaTagsSlice.selector], meta => meta ?? EMPTY_META_TAGS)

/**
 * A sitter's meta tags, keyed by both the ids a caller has: the numeric one and the token the
 * public profile is routed by. The api sends them as a separate entity referenced from the user,
 * which is why this cannot be a plain entry in the entity table.
 */
export const updateUserMetaTagsSlice = (payload: unknown) =>
  userMetaTagsSlice.update(state => {
    const { meta_tags, users = {} } = normalizeSafe(payload)
    if (!meta_tags) return state

    const byUserId = Object.fromEntries(
      Object.values(users).flatMap(user => {
        if (user.attributes.account_type !== 'sitter') return []
        const meta_id = 'meta_tags' in user.relationships ? user.relationships.meta_tags.data?.id : null
        if (!meta_id) return []
        const meta = meta_tags[meta_id]
        if (!meta) throw new Error(`Meta for user ${user.id} not found`)
        return [
          [user.id, meta],
          [user.attributes.token, meta],
        ]
      })
    )

    return mergeMaps(state ?? EMPTY_META_TAGS, byUserId)
  })

const normalizeSafe = (payload: unknown) => {
  try {
    return normalize(payload as Parameters<typeof normalize>[0]) as {
      meta_tags?: { [id: string]: ApiMetaTags }
      users?: { [id: string]: ApiUser }
    }
  } catch {
    return {}
  }
}

const EMPTY_META_TAGS: { [key: string]: ApiMetaTags } = {}
